/**
 * Template detail — SKNOTIFICATION-GLOBAL-CR-001 UICC-001 §3-5: the variant
 * grid (locale x channel authoring + live placeholder-parity warnings), the
 * Gate 1 approval matrix, and the Gate 2 permission-cube editor, all for one
 * template. Approval/permission edits are gated to admin-ish roles — those
 * are claims about external reality or jurisdictional legal policy, not a
 * plain member's call (see sk-notification-api's GovernanceController,
 * which enforces the same split server-side; this is UX, not the boundary).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, StyleSheet } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import { useTheme } from '../../context/themeContext';
import { useResponsive } from '../../lib/responsive';
import { useToast } from '../../components/toast';
import Icon from '../../components/notif/Icon';
import { PageHeader, EmptyState, Btn, Banner, SectionLabel } from '../../components/notif/ui';
import { Select } from '../../components/notif/select';
import { useSession } from '../../context/sessionContext';
import { useApp } from '../../context/appContext';
import { isRTLLocale, isolateLTR } from '../../lib/rtl';
import { sampleValueFor } from '../../lib/format';
import {
  getTemplateDetail,
  upsertVariant,
  setApproval,
  setPermission,
} from '../../services/notification/governance';
import { handleApiError } from '../../utils/errorHandler';
import {
  GOVERNANCE_CHANNELS,
  MESSAGE_CLASSES,
  APPROVAL_STATES,
} from '../../schema/governance';
import type {
  BackendTemplateDetail,
  BackendTemplateVariant,
  ApprovalState,
  MessageClassCode,
} from '../../schema/governance';
import type { ChannelType } from '../../schema/notification';
import type { AppStackParamList } from '../../navigation/types';

const ADMIN_ROLES = new Set(['org_admin', 'admin', 'platform_admin']);

export default function TemplateDetailScreen() {
  const { theme: { colors, font } } = useTheme();
  const { pad } = useResponsive();
  const toast = useToast();
  const { role } = useSession();
  const canManageGates = role != null && ADMIN_ROLES.has(role);
  const { params } = useRoute<RouteProp<AppStackParamList, 'templateDetail'>>();
  const { templateId } = params;

  const [detail, setDetail] = useState<BackendTemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = () => {
    setLoading(true);
    setErr(false);
    getTemplateDetail(templateId)
      .then(setDetail)
      .catch((e) => { setErr(true); handleApiError(e, "Couldn't load template"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [templateId]);

  if (loading) return <EmptyState title="Loading template…" />;
  if (err || !detail) return <EmptyState icon="alert" title="Couldn't load template" action={<Btn label="Retry" onPress={load} />} />;

  const { template, variants, approvals, permissions } = detail;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 20 }}>
      <PageHeader
        title={template.displayName}
        subtitle={`${template.templateKey} · ${template.messageClassCode}${template.isSystem ? ' · system' : ''}`}
        right={<Btn label="Refresh" icon="refresh" size="sm" onPress={load} />}
      />

      <VariantEditor templateId={templateId} variants={variants} onSaved={load} colors={colors} font={font} />

      <ApprovalMatrix
        templateId={templateId}
        approvals={approvals}
        canManage={canManageGates}
        onSaved={load}
        colors={colors}
      />

      <PermissionCube
        templateId={templateId}
        messageClassCode={template.messageClassCode}
        permissions={permissions}
        canManage={canManageGates}
        onSaved={load}
        colors={colors}
      />
    </ScrollView>
  );
}

// ── Variant editor ────────────────────────────────────────────────────────────

function VariantEditor({
  templateId, variants, onSaved, colors, font,
}: {
  templateId: string;
  variants: BackendTemplateVariant[];
  onSaved: () => void;
  colors: any;
  font: any;
}) {
  const toast = useToast();
  const { locale: consoleLocale } = useApp();
  const [localeTag, setLocaleTag] = useState('en');
  const [channelKind, setChannelKind] = useState<ChannelType>('whatsapp');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [placeholders, setPlaceholders] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  // Live preview (UICC-001 §3: "kit formatter for a live preview — money,
  // dates rendered per the chosen locale" + "LTR-isolate placeholders in an
  // RTL preview"). Direction follows the VARIANT's own locale (what the
  // recipient will read), not the console's own UI locale.
  const previewIsRTL = isRTLLocale(localeTag);
  const previewText = React.useMemo(() => {
    const keys = placeholders.split(',').map((p) => p.trim()).filter(Boolean);
    let rendered = body;
    for (const key of keys) {
      const sample = sampleValueFor(key, consoleLocale);
      const value = previewIsRTL ? isolateLTR(sample) : sample;
      rendered = rendered.split(`{${key}}`).join(value);
    }
    return rendered;
  }, [body, placeholders, consoleLocale, previewIsRTL]);

  const editExisting = (v: BackendTemplateVariant) => {
    setLocaleTag(v.localeTag);
    setChannelKind(v.channelKind);
    setSubject(v.subject ?? '');
    setBody(v.body);
    setPlaceholders(v.placeholderKeys.join(', '));
    setWarnings([]);
  };

  const canSave = !!localeTag.trim() && !!body.trim() && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const placeholderKeys = placeholders.split(',').map((p) => p.trim()).filter(Boolean);
      const result = await upsertVariant(templateId, {
        localeTag: localeTag.trim(),
        channelKind,
        subject: subject.trim() || undefined,
        body,
        placeholderKeys,
      });
      setWarnings(result.placeholderWarnings);
      if (result.approvalsReset.length > 0) {
        toast.info('Approval reset', {
          message: `Editing this content reset approval for: ${result.approvalsReset.map((a) => `${a.channelKind}/${a.regionCode}`).join(', ')}`,
        });
      } else {
        toast.success('Variant saved');
      }
      onSaved();
    } catch (e) {
      handleApiError(e, 'Could not save variant');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = [st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }];

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Variants — locale × channel content</SectionLabel>

      {variants.length === 0 ? (
        <Text style={{ color: colors.ink4, fontSize: 12, marginBottom: 12 }}>No variants yet — add one below.</Text>
      ) : (
        <View style={{ marginBottom: 14 }}>
          {variants.map((v) => (
            <Pressable
              key={v.templateVariantId}
              onPress={() => editExisting(v)}
              style={[st.variantRow, { borderColor: colors.line, backgroundColor: colors.paper2 }]}
            >
              <Text style={{ color: colors.ink, fontSize: 12, fontWeight: '600', width: 50 }}>{v.localeTag}</Text>
              <Text style={{ color: colors.ink2, fontSize: 12, width: 80, textTransform: 'capitalize' }}>{v.channelKind}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12, flex: 1 }} numberOfLines={1}>{v.body}</Text>
              <Icon name="chevronRight" size={13} color={colors.ink3} />
            </Pressable>
          ))}
        </View>
      )}

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <View style={{ width: 90 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Locale</Text>
          <TextInput value={localeTag} onChangeText={setLocaleTag} placeholder="en" placeholderTextColor={colors.ink4} style={inputStyle} />
        </View>
        <View style={{ width: 140 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Channel</Text>
          <Select value={channelKind} onChange={setChannelKind} options={GOVERNANCE_CHANNELS} />
        </View>
        <View style={{ flex: 1, minWidth: 200 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Subject (email only)</Text>
          <TextInput value={subject} onChangeText={setSubject} placeholder="Optional" placeholderTextColor={colors.ink4} style={inputStyle} />
        </View>
      </View>

      <View style={{ marginTop: 12 }}>
        <Text style={[st.label, { color: colors.ink3 }]}>Body</Text>
        <TextInput
          value={body}
          onChangeText={setBody}
          multiline
          placeholder="Hi {name}, 20% off this weekend!"
          placeholderTextColor={colors.ink4}
          style={[st.textarea, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper, fontFamily: font.mono }]}
        />
      </View>

      <View style={{ marginTop: 12 }}>
        <Text style={[st.label, { color: colors.ink3 }]}>Placeholders (comma-separated)</Text>
        <TextInput value={placeholders} onChangeText={setPlaceholders} placeholder="name, amount" placeholderTextColor={colors.ink4} style={inputStyle} />
      </View>

      {!!body.trim() && (
        <View style={{ marginTop: 12 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>
            Preview{previewIsRTL ? ' (RTL — placeholders LTR-isolated)' : ''}
          </Text>
          <View style={[st.preview, { borderColor: colors.line, backgroundColor: colors.paper2 }]}>
            {!!subject.trim() && channelKind === 'email' && (
              <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 6, writingDirection: previewIsRTL ? 'rtl' : 'ltr' }}>
                {subject}
              </Text>
            )}
            <Text
              style={{
                color: colors.ink, fontSize: 13,
                textAlign: previewIsRTL ? 'right' : 'left',
                writingDirection: previewIsRTL ? 'rtl' : 'ltr',
              }}
            >
              {previewText}
            </Text>
          </View>
        </View>
      )}

      {warnings.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Banner tone="warning" icon="alert">
            <Text style={{ color: colors.ink, fontSize: 12, fontWeight: '600' }}>Placeholder mismatch</Text>
            {warnings.map((w, i) => <Text key={i} style={{ color: colors.ink3, fontSize: 11, marginTop: 2 }}>{w}</Text>)}
          </Banner>
        </View>
      )}

      <View style={{ marginTop: 14 }}>
        <Btn label={saving ? 'Saving…' : 'Save variant'} icon="check" kind="primary" disabled={!canSave} onPress={save} />
      </View>
    </View>
  );
}

// ── Gate 1 — approval matrix ──────────────────────────────────────────────────

function ApprovalMatrix({
  templateId, approvals, canManage, onSaved, colors,
}: {
  templateId: string;
  approvals: import('../../schema/governance').BackendTemplateApproval[];
  canManage: boolean;
  onSaved: () => void;
  colors: any;
}) {
  const toast = useToast();
  const [channelKind, setChannelKind] = useState<ChannelType>('whatsapp');
  const [regionCode, setRegionCode] = useState('IN');
  const [approvalState, setApprovalState] = useState<ApprovalState>('submitted');
  const [externalTemplateId, setExternalTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await setApproval(templateId, {
        channelKind,
        regionCode: regionCode.trim().toUpperCase(),
        approvalState,
        externalTemplateId: externalTemplateId.trim() || undefined,
      });
      toast.success('Approval updated', { message: `${channelKind}/${regionCode.toUpperCase()} → ${approvalState}` });
      setExternalTemplateId('');
      onSaved();
    } catch (e) {
      handleApiError(e, 'Could not set approval');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Gate 1 — approval, per channel × region</SectionLabel>
      <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 12 }}>
        Reflects external registration (Meta/DLT). Editing an approved variant's content resets its state here to draft.
      </Text>

      {approvals.length === 0 ? (
        <Text style={{ color: colors.ink4, fontSize: 12, marginBottom: 12 }}>No approval rows yet.</Text>
      ) : (
        <View style={{ marginBottom: 14 }}>
          {approvals.map((a) => (
            <View key={`${a.channelKind}:${a.regionCode}`} style={[st.tr, { borderBottomColor: colors.line }]}>
              <Text style={{ color: colors.ink, fontSize: 12, width: 90, textTransform: 'capitalize' }}>{a.channelKind}</Text>
              <Text style={{ color: colors.ink2, fontSize: 12, width: 60 }}>{a.regionCode}</Text>
              <StateChip state={a.approvalState} colors={colors} />
              {!!a.externalTemplateId && (
                <Text style={{ color: colors.ink4, fontSize: 11, flex: 1 }} numberOfLines={1}>{a.externalTemplateId}</Text>
              )}
            </View>
          ))}
        </View>
      )}

      {canManage ? (
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <View style={{ width: 140 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Channel</Text>
            <Select value={channelKind} onChange={setChannelKind} options={GOVERNANCE_CHANNELS} />
          </View>
          <View style={{ width: 80 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Region</Text>
            <TextInput value={regionCode} onChangeText={setRegionCode} placeholder="IN" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
          </View>
          <View style={{ width: 150 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>State</Text>
            <Select value={approvalState} onChange={setApprovalState} options={APPROVAL_STATES} />
          </View>
          <View style={{ flex: 1, minWidth: 160 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>External template ID</Text>
            <TextInput value={externalTemplateId} onChangeText={setExternalTemplateId} placeholder="Optional — Meta/DLT id" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
          </View>
          <Btn label={saving ? 'Saving…' : 'Set approval'} kind="primary" size="sm" disabled={saving} onPress={save} />
        </View>
      ) : (
        <Text style={{ color: colors.ink4, fontSize: 11 }}>Only an admin can change approval state.</Text>
      )}
    </View>
  );
}

// ── Gate 2 — permission cube ──────────────────────────────────────────────────

function PermissionCube({
  templateId, messageClassCode, permissions, canManage, onSaved, colors,
}: {
  templateId: string;
  messageClassCode: MessageClassCode;
  permissions: import('../../schema/governance').BackendTemplatePermission[];
  canManage: boolean;
  onSaved: () => void;
  colors: any;
}) {
  const toast = useToast();
  const [jurisdictionCode, setJurisdictionCode] = useState('IN');
  const [channelKind, setChannelKind] = useState<ChannelType>('whatsapp');
  const [classCode, setClassCode] = useState<MessageClassCode>(messageClassCode);
  const [isPermitted, setIsPermitted] = useState(true);
  const [reasonCode, setReasonCode] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await setPermission(templateId, {
        jurisdictionCode: jurisdictionCode.trim().toUpperCase(),
        channelKind,
        messageClassCode: classCode,
        isPermitted,
        reasonCode: reasonCode.trim() || undefined,
      });
      toast.success('Permission updated', {
        message: `${jurisdictionCode.toUpperCase()}/${channelKind}/${classCode} → ${isPermitted ? 'permitted' : 'denied'}`,
      });
      setReasonCode('');
      onSaved();
    } catch (e) {
      handleApiError(e, 'Could not set permission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Gate 2 — permission cube (jurisdiction × channel × class)</SectionLabel>
      <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 12 }}>
        Default-deny: unset means denied. The Dubai/India rule — permit IN, leave AE unset.
      </Text>

      {permissions.length === 0 ? (
        <Text style={{ color: colors.ink4, fontSize: 12, marginBottom: 12 }}>
          No rows yet — every jurisdiction is denied by default until permitted below.
        </Text>
      ) : (
        <View style={{ marginBottom: 14, flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {permissions.map((p) => (
            <View
              key={`${p.jurisdictionCode}:${p.channelKind}:${p.messageClassCode}`}
              style={[
                st.cubeCell,
                {
                  backgroundColor: p.isPermitted ? '#EBF6F1' : '#F1F5F9',
                  borderColor: p.isPermitted ? '#2D8A5F55' : colors.line,
                },
              ]}
            >
              <Text style={{ color: p.isPermitted ? '#2D8A5F' : colors.ink4, fontSize: 12, fontWeight: '700' }}>
                {p.jurisdictionCode}
              </Text>
              <Text style={{ color: colors.ink3, fontSize: 10, textTransform: 'capitalize' }}>{p.channelKind}</Text>
              <Text style={{ color: colors.ink4, fontSize: 10 }}>{p.messageClassCode}</Text>
              <Text style={{ color: p.isPermitted ? '#2D8A5F' : '#C0392B', fontSize: 10, fontWeight: '600' }}>
                {p.isPermitted ? 'permitted' : 'denied'}
              </Text>
              {!!p.reasonCode && <Text style={{ color: colors.ink4, fontSize: 9 }} numberOfLines={1}>{p.reasonCode}</Text>}
            </View>
          ))}
        </View>
      )}

      {canManage ? (
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <View style={{ width: 90 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Jurisdiction</Text>
            <TextInput value={jurisdictionCode} onChangeText={setJurisdictionCode} placeholder="IN" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
          </View>
          <View style={{ width: 140 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Channel</Text>
            <Select value={channelKind} onChange={setChannelKind} options={GOVERNANCE_CHANNELS} />
          </View>
          <View style={{ width: 150 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Message class</Text>
            <Select value={classCode} onChange={setClassCode} options={MESSAGE_CLASSES} />
          </View>
          <View style={{ width: 130 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Decision</Text>
            <Select
              value={isPermitted ? 'permit' : 'deny'}
              onChange={(v) => setIsPermitted(v === 'permit')}
              options={[{ value: 'permit', label: 'Permit' }, { value: 'deny', label: 'Deny' }]}
            />
          </View>
          <View style={{ flex: 1, minWidth: 160 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Reason (for a deny)</Text>
            <TextInput value={reasonCode} onChangeText={setReasonCode} placeholder="Optional" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
          </View>
          <Btn label={saving ? 'Saving…' : 'Set permission'} kind="primary" size="sm" disabled={saving} onPress={save} />
        </View>
      ) : (
        <Text style={{ color: colors.ink4, fontSize: 11 }}>Only an admin can change jurisdictional permission.</Text>
      )}
    </View>
  );
}

function StateChip({ state, colors }: { state: ApprovalState; colors: any }) {
  const tone: Record<ApprovalState, string> = {
    draft: colors.ink4, submitted: '#B87D0A', approved: '#2D8A5F', rejected: '#C94444', paused: '#B87D0A',
  };
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 2, backgroundColor: tone[state] + '22', width: 90 }}>
      <Text style={{ color: tone[state], fontSize: 11, fontWeight: '600' }}>{state}</Text>
    </View>
  );
}

const st = StyleSheet.create({
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  label: { fontSize: 11, fontWeight: '600', marginBottom: 5 },
  input: { height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, fontSize: 13 },
  textarea: { minHeight: 90, borderWidth: 1, borderRadius: 2, padding: 10, fontSize: 13, textAlignVertical: 'top' },
  tr: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, gap: 10 },
  variantRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderWidth: 1, borderRadius: 2, marginBottom: 6 },
  cubeCell: { padding: 8, borderRadius: 2, borderWidth: 1, minWidth: 100, gap: 2 },
  preview: { borderWidth: 1, borderRadius: 2, padding: 12 },
});
